import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Globe, ArrowRight, Brain, Activity, Mail, ZoomIn } from "lucide-react";
import Navigation from "@/components/landing/Navigation";
import Footer from "@/components/landing/Footer";
import { ImageZoomModal } from "@/components/landing/ImageZoomModal";
import { useTranslation } from "@/hooks/useTranslation";
import { useEffect, useMemo, useState } from "react";
import { arMiddleEast } from "@/i18n/ar-middle-east";
import vida24Screens from "@/assets/vida24-partner-overview.png";
import aiDecisionSupport from "@/assets/ai-decision-support.png";

const vida24AppMockup = "/lovable-uploads/66f7b12f-9c3f-451d-8916-47c1764be385.png";

const ISOLATED_TERMS = ['VIDAVO', 'VIDA24', 'الذكاء الاصطناعي'];

const bdiText = (text: string): React.ReactNode => {
  const pattern = new RegExp(`(${ISOLATED_TERMS.join('|')})`, 'g');
  const parts = text.split(pattern);
  return parts.map((part, i) =>
    ISOLATED_TERMS.includes(part) ? <bdi key={i}>{part}</bdi> : part
  );
};

const paragraphs = (text: string, isArabic = false) =>
  text.split("\n\n").map((p, i) => (
    <p
      key={i}
      className={`text-neutral-700 mb-4 last:mb-0 ${
        isArabic ? "text-lg sm:text-xl leading-loose text-right" : "text-base sm:text-lg leading-relaxed"
      }`}
    >
      {bdiText(p)}
    </p>
  ));

const heroParagraphs = (text: string, isArabic = false) =>
  text.split("\n\n").map((p, i) => (
    <p
      key={i}
      className={`w-full sm:w-0 sm:min-w-full text-white/90 mb-4 last:mb-0 ${
        isArabic ? "text-lg sm:text-xl leading-loose text-right" : "text-base sm:text-lg leading-relaxed"
      }`}
    >
      {bdiText(p)}
    </p>
  ));

const VidavoMiddleEast = () => {
  const { t } = useTranslation();

  const isArabic = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('lang') === 'ar';
  }, []);

  const d = isArabic ? arMiddleEast : t.vidavoMiddleEast;
  const platformImage = isArabic ? vida24AppMockup : vida24Screens;

  // Rotating tagline, identical to the homepage hero
  const suffixes = [t.connectedCareHero.anytime, t.connectedCareHero.anywhere];
  const arSuffixes = [arMiddleEast.heroSuffixAnytime, arMiddleEast.heroSuffixAnywhere];
  const arBase = arMiddleEast.heroBase;
  const [currentSuffixIndex, setCurrentSuffixIndex] = useState(0);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [isZoomOpen, setIsZoomOpen] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    const handleChange = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    if (prefersReducedMotion) return;
    const interval = setInterval(() => {
      setCurrentSuffixIndex((prev) => (prev + 1) % suffixes.length);
    }, 1750);
    return () => clearInterval(interval);
  }, [prefersReducedMotion, suffixes.length]);

  return (
    <div dir={isArabic ? "rtl" : "ltr"} className={`min-h-screen bg-gradient-to-br from-neutral-100 to-neutral-50 relative overflow-hidden ${isArabic ? "font-arabic" : ""}`}>
      <Helmet>
        <title>{d.pageTitle}</title>
        <meta name="description" content={d.metaDescription} />
        <link rel="canonical" href="https://vidavo.eu/about/vidavo-middle-east" />
        <meta property="og:title" content={d.pageTitle} />
        <meta property="og:description" content={d.metaDescription} />
        <meta property="og:url" content="https://vidavo.eu/about/vidavo-middle-east" />
        <meta property="og:type" content="website" />
      </Helmet>

      <Navigation />

      {/* Hero */}
      <header className="relative min-h-[60vh] flex items-center pt-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/lovable-uploads/e1aa93b9-184a-4db5-93e2-f30156e3e8bb.png"
            alt={d.heroImageAlt}
            loading="eager"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-primary/50 via-primary/40 to-secondary/50" />
        </div>

        <div className="container-padding relative z-10 py-12 md:py-20 w-full">
          <div className="max-w-5xl mx-auto text-center relative">
            {/* Hindi button — absolute top-right on desktop */}
            {!isArabic && (
              <a
                href="/about/vidavo-middle-east?lang=ar"
                target="_blank"
                rel="noopener noreferrer"
                className="hidden md:inline-flex absolute top-0 right-0 items-center gap-2 px-4 py-2 rounded-full bg-white text-primary text-sm font-bold shadow-[0_4px_20px_-4px_rgba(255,255,255,0.5)] border border-white/80 hover:bg-white/90 hover:shadow-[0_6px_24px_-4px_rgba(255,255,255,0.6)] transition-all duration-300 hover:scale-105"
              >
                <Globe className="w-4 h-4" />
                {t.vidavoMiddleEast.readInArabic}
              </a>
            )}

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-6 py-3 text-white font-medium backdrop-blur-sm mb-8"
            >
              <Globe className="w-4 h-4" />
              {bdiText(d.badge)}
            </motion.div>

            {/* Hindi button — inline on mobile */}
            {!isArabic && (
              <a
                href="/about/vidavo-middle-east?lang=ar"
                target="_blank"
                rel="noopener noreferrer"
                className="md:hidden inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-primary text-sm font-bold shadow-[0_4px_20px_-4px_rgba(255,255,255,0.5)] border border-white/80 hover:bg-white/90 hover:shadow-[0_6px_24px_-4px_rgba(255,255,255,0.6)] transition-all duration-300 hover:scale-105 mb-8"
              >
                <Globe className="w-4 h-4" />
                {t.vidavoMiddleEast.readInArabic}
              </a>
            )}

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-8 leading-tight flex flex-wrap items-baseline justify-center"
              style={{ fontSize: 'clamp(1.75rem, 4.5vw, 4rem)' }}
              aria-live="polite"
              aria-atomic="true"
            >
              {isArabic ? (
                prefersReducedMotion ? (
                  <span className="py-[10px]">{`${arBase} ${arSuffixes[0]} ${arSuffixes[1]}`}</span>
                ) : (
                  <>
                    <span className="py-[10px]">{arBase}</span>
                    <span className="inline-block whitespace-nowrap">
                      <span
                        key={currentSuffixIndex}
                        style={{ animationDuration: '1s' }}
                        className="animate-fade-in py-[10px]"
                      >
                        {arSuffixes[currentSuffixIndex]}
                      </span>
                    </span>
                  </>
                )
              ) : prefersReducedMotion ? (
                t.connectedCareHero.fullHeadline
              ) : (
                <>
                  <span className="py-[10px]">{t.connectedCareHero.healthForAll}</span>
                  <span className="inline-block whitespace-nowrap">
                    <span
                      key={currentSuffixIndex}
                      style={{ animationDuration: '1s' }}
                      className="animate-fade-in py-[10px]"
                    >
                      {suffixes[currentSuffixIndex]}
                    </span>
                  </span>
                </>
              )}
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-[1.25] mb-6"
            >
              {bdiText(d.heroTitle)}
            </motion.h1>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className={`grid w-full sm:w-fit sm:inline-grid sm:max-w-full mx-auto p-6 sm:p-8 rounded-3xl border border-white/20 bg-white/10 backdrop-blur-lg shadow-[0_8px_40px_-12px_rgba(0,0,0,0.15)] ${isArabic ? "text-right" : "text-left"}`}
            >
              <h2 className={`text-xl sm:text-2xl md:text-3xl font-bold text-white mb-4 whitespace-normal sm:whitespace-nowrap ${isArabic ? "leading-relaxed" : "leading-tight"}`}>
                {bdiText(d.heroLead)}
              </h2>
              {heroParagraphs(d.heroText, isArabic)}
            </motion.div>
          </div>
        </div>
      </header>

      {/* VIDA24 platform */}
      <section className="container-padding py-16 md:py-24 relative z-10">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.4 }}
            className={`max-w-full text-center ${isArabic ? "[&_p]:text-right" : "[&_p]:text-center"}`}
          >
            <div className="inline-flex items-center gap-2 text-secondary font-medium mb-4">
              <Activity className="w-5 h-5" />
              <bdi>VIDA24</bdi>
            </div>
            <h2 className={`text-2xl sm:text-3xl md:text-4xl font-bold text-accent-teal mb-6 ${isArabic ? "leading-relaxed" : "leading-[1.3]"}`}>
              {bdiText(d.platformTitle)}
            </h2>
            {paragraphs(d.platformText, isArabic)}
          </motion.div>

          <motion.button
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.4 }}
            onClick={() => setIsZoomOpen(true)}
            className="relative mt-10 md:mt-14 max-w-4xl mx-auto block w-full group cursor-zoom-in"
            aria-label="Tap to zoom"
            type="button"
          >
            <img
              src={platformImage}
              alt={d.applicationImageAlt}
              loading="lazy"
              className="w-full h-auto object-contain rounded-2xl"
            />
            <span className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/60 text-white/90 text-sm backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              <ZoomIn className="w-4 h-4" />
              Tap to zoom
            </span>
          </motion.button>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="mt-10 md:mt-14 text-center"
          >
            <a
              href="https://vida24.eu/"
              target="_blank"
              rel="noopener noreferrer"
              className="button-primary group inline-flex items-center gap-2"
            >
              {d.platformCta}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* AI section */}
      <section className="container-padding py-16 md:py-24 relative z-10">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.4 }}
            className="order-2 lg:order-1 rounded-3xl overflow-hidden border border-secondary/15 bg-white/70 backdrop-blur-lg shadow-[0_8px_40px_-12px_rgba(102,179,187,0.25)] p-4"
          >
            <img
              src={aiDecisionSupport}
              alt="AI decision support dashboard for healthcare insights"
              loading="lazy"
              className="w-full h-auto object-contain rounded-2xl"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.4 }}
            className="order-1 lg:order-2"
          >
            <div className="inline-flex items-center gap-2 text-secondary font-medium mb-4">
              <Brain className="w-5 h-5" />
              <bdi>{d.aiBadge}</bdi>
            </div>
            <h2 className={`text-2xl sm:text-3xl md:text-4xl font-bold text-accent-teal mb-6 ${isArabic ? "leading-relaxed text-right" : "leading-[1.3]"}`}>
              {bdiText(d.aiTitle)}
            </h2>
            {paragraphs(`${d.aiText1}\n\n${d.aiText2}`, isArabic)}
          </motion.div>
        </div>
      </section>

      {/* Future + CTA */}
      <section className="container-padding pb-20 md:pb-28 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.4 }}
          className="max-w-4xl mx-auto text-center p-8 sm:p-12 rounded-3xl border border-secondary/15 bg-white/60 backdrop-blur-lg shadow-[0_8px_40px_-12px_rgba(102,179,187,0.2)]"
        >
          <h2 className={`text-2xl sm:text-3xl md:text-4xl font-bold text-accent-teal mb-6 ${isArabic ? "leading-relaxed" : "leading-[1.3]"}`}>
            {bdiText(d.futureTitle)}
          </h2>
          <p className={`text-neutral-700 mb-10 ${isArabic ? "text-lg sm:text-xl leading-loose text-right" : "text-base sm:text-lg leading-relaxed"}`}>{bdiText(d.futureText)}</p>

          <h3 className={`text-xl sm:text-2xl font-semibold text-secondary mb-3 ${isArabic ? "leading-relaxed" : ""}`}>{bdiText(d.ctaTitle)}</h3>
          <p className={`text-neutral-700 mb-8 ${isArabic ? "text-lg leading-loose text-right" : "text-base"}`}>{bdiText(d.ctaText)}</p>
          <a
            href="mailto:info@vidavo.eu?subject=VIDAVO%20Middle%20East%20-%20Inquiry"
            className="button-primary group inline-flex items-center gap-2"
          >
            <Mail className="w-4 h-4" />
            {bdiText(d.ctaButton)}
          </a>
        </motion.div>
      </section>


      <Footer />

      <ImageZoomModal
        src={platformImage}
        alt={d.applicationImageAlt}
        isOpen={isZoomOpen}
        onClose={() => setIsZoomOpen(false)}
        dir={isArabic ? "rtl" : "ltr"}
        hint="Tap to zoom"
      />
    </div>
  );
};

export default VidavoMiddleEast;